import React, { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { portalRequest } from '../../utils/portalApi'
import { 
  FileText, DollarSign, Clock, CheckCircle, XCircle, ExternalLink, 
  CreditCard, AlertCircle, ChevronDown, ChevronUp, Filter, Plus, X,
  Users, Send
} from 'lucide-react'

const statusConfig = {
  paid: { label: 'Paid', color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: CheckCircle },
  pending: { label: 'Pending', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Clock },
  overdue: { label: 'Overdue', color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20', icon: XCircle },
  draft: { label: 'Draft', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20', icon: FileText },
  partial: { label: 'Partial', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: CreditCard },
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount / 100)
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function PortalInvoices() {
  const { team, canManage, token } = useOutletContext()
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [expandedId, setExpandedId] = useState(null)
  const [payingId, setPayingId] = useState(null)

  // Create invoice state
  const [showCreate, setShowCreate] = useState(false)
  const [showConnectPrompt, setShowConnectPrompt] = useState(false)
  const [creating, setCreating] = useState(false)
  const [roster, setRoster] = useState([])
  const [rosterLoading, setRosterLoading] = useState(false)
  const [newInvoice, setNewInvoice] = useState({
    title: '',
    description: '',
    amount: '',
    due_date: '',
    payment_type: 'one_time',
    subscription_interval: 'monthly',
    installment_count: 2,
    recipient_ids: [],
  })

  useEffect(() => {
    if (team?.team_id) fetchInvoices()
  }, [team?.team_id])

  const fetchRoster = async () => {
    if (roster.length > 0) return
    setRosterLoading(true)
    try {
      const data = await portalRequest(`/api/teams/${team.team_id}/roster`)
      const members = (Array.isArray(data) ? data : data?.members || []).filter(
        m => m.status === 'active'
      )
      setRoster(members)
    } catch (err) {
      console.error('Error fetching roster:', err)
    } finally {
      setRosterLoading(false)
    }
  }

  const openCreateModal = () => {
    // Require Stripe Connect before creating invoices
    if (!team?.stripe_connect_payouts_enabled) {
      setShowConnectPrompt(true)
      return
    }
    setNewInvoice({
      title: '',
      description: '',
      amount: '',
      due_date: '',
      payment_type: 'one_time',
      subscription_interval: 'monthly',
      installment_count: 2,
      recipient_ids: [],
    })
    setShowCreate(true)
    fetchRoster()
  }

  const toggleRecipient = (userId) => {
    setNewInvoice(prev => ({
      ...prev,
      recipient_ids: prev.recipient_ids.includes(userId)
        ? prev.recipient_ids.filter(id => id !== userId)
        : [...prev.recipient_ids, userId]
    }))
  }

  const selectAllRecipients = () => {
    const allIds = roster.map(m => m.user_id)
    const allSelected = allIds.every(id => newInvoice.recipient_ids.includes(id))
    setNewInvoice(prev => ({
      ...prev,
      recipient_ids: allSelected ? [] : allIds
    }))
  }

  const handleCreateInvoice = async () => {
    if (!newInvoice.title.trim()) return alert('Title is required')
    if (!newInvoice.amount || parseFloat(newInvoice.amount) <= 0) return alert('Valid amount is required')
    if (newInvoice.recipient_ids.length === 0) return alert('Select at least one recipient')

    setCreating(true)
    try {
      const payload = {
        team_id: team.team_id,
        title: newInvoice.title.trim(),
        description: newInvoice.description.trim() || null,
        amount: parseFloat(newInvoice.amount),
        due_date: newInvoice.due_date || null,
        recipient_ids: newInvoice.recipient_ids,
        payment_type: newInvoice.payment_type,
      }
      if (newInvoice.payment_type === 'subscription') {
        payload.subscription_interval = newInvoice.subscription_interval
      }
      if (newInvoice.payment_type === 'payment_plan') {
        payload.installment_count = parseInt(newInvoice.installment_count)
      }
      await portalRequest(`/api/payments/teams/${team.team_id}/invoices`, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      setShowCreate(false)
      fetchInvoices()
    } catch (err) {
      alert(err.message || 'Failed to create invoice')
    } finally {
      setCreating(false)
    }
  }

  const fetchInvoices = async () => {
    try {
      const data = await portalRequest(`/api/payments/teams/${team.team_id}/invoices`)
      setInvoices(Array.isArray(data) ? data : data?.invoices || [])
    } catch (err) {
      console.error('Error fetching invoices:', err)
    } finally {
      setLoading(false)
    }
  }

  const handlePay = async (invoice) => {
    setPayingId(invoice.invoice_id || invoice._id)
    try {
      // If there's a Stripe checkout URL, redirect to it
      if (invoice.checkout_url) {
        window.open(invoice.checkout_url, '_blank')
        return
      }
      // Otherwise create a checkout session
      const data = await portalRequest(`/api/payments/create-checkout`, {
        method: 'POST',
        body: JSON.stringify({
          team_id: team.team_id,
          invoice_id: invoice.invoice_id || invoice._id
        })
      })
      if (data?.checkout_url || data?.url) {
        window.open(data.checkout_url || data.url, '_blank')
      }
    } catch (err) {
      console.error('Checkout error:', err)
    } finally {
      setPayingId(null)
    }
  }

  const filtered = filter === 'all' ? invoices : invoices.filter(inv => inv.status === filter)
  
  const totalDue = invoices
    .filter(inv => ['pending', 'overdue', 'partial'].includes(inv.status))
    .reduce((sum, inv) => sum + (inv.amount_remaining || inv.amount || 0), 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Invoices & Payments</h1>
          <p className="text-zinc-400 mt-1">{canManage ? 'Create and manage team invoices' : 'View and pay team invoices'}</p>
        </div>
        {canManage && (
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all text-sm shadow-lg shadow-green-500/20"
          >
            <Plus className="w-4 h-4" />
            Create Invoice
          </button>
        )}
      </div>

      {/* Create Invoice Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-zinc-800 rounded-2xl border border-zinc-700 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-zinc-700">
              <h2 className="text-xl font-bold text-white">Create Invoice</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 rounded-lg hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Title *</label>
                <input
                  type="text"
                  value={newInvoice.title}
                  onChange={e => setNewInvoice(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
                  placeholder="e.g. Monthly Team Dues, Tournament Fee"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Description</label>
                <textarea
                  value={newInvoice.description}
                  onChange={e => setNewInvoice(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all resize-none"
                  rows={2}
                  placeholder="Optional details about this invoice"
                />
              </div>

              {/* Amount + Due Date row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Amount ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newInvoice.amount}
                    onChange={e => setNewInvoice(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Due Date</label>
                  <input
                    type="date"
                    value={newInvoice.due_date}
                    onChange={e => setNewInvoice(prev => ({ ...prev, due_date: e.target.value }))}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
                  />
                </div>
              </div>

              {/* Payment Type */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Payment Type</label>
                <div className="flex gap-2">
                  {[{ value: 'one_time', label: 'One-Time' }, { value: 'subscription', label: 'Recurring' }, { value: 'payment_plan', label: 'Payment Plan' }].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setNewInvoice(prev => ({ ...prev, payment_type: opt.value }))}
                      className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                        newInvoice.payment_type === opt.value
                          ? 'bg-green-500/10 text-green-400 border-green-500/30'
                          : 'text-zinc-400 border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subscription interval */}
              {newInvoice.payment_type === 'subscription' && (
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Billing Interval</label>
                  <select
                    value={newInvoice.subscription_interval}
                    onChange={e => setNewInvoice(prev => ({ ...prev, subscription_interval: e.target.value }))}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              )}

              {/* Installment count */}
              {newInvoice.payment_type === 'payment_plan' && (
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Number of Installments</label>
                  <select
                    value={newInvoice.installment_count}
                    onChange={e => setNewInvoice(prev => ({ ...prev, installment_count: e.target.value }))}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
                  >
                    {[2,3,4,5,6,7,8,9,10,11,12].map(n => (
                      <option key={n} value={n}>{n} payments{newInvoice.amount ? ` (${formatCurrency(Math.round((parseFloat(newInvoice.amount) / n) * 100))}/ea)` : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Recipients */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-white flex items-center gap-2">
                    <Users className="w-4 h-4 text-zinc-400" />
                    Recipients * ({newInvoice.recipient_ids.length} selected)
                  </label>
                  <button
                    onClick={selectAllRecipients}
                    className="text-xs text-green-400 hover:text-green-300 font-medium"
                  >
                    {roster.length > 0 && newInvoice.recipient_ids.length === roster.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                {rosterLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500"></div>
                  </div>
                ) : roster.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-4 text-center">No team members found</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto rounded-xl border border-zinc-700 divide-y divide-zinc-700">
                    {roster.map(member => {
                      const isSelected = newInvoice.recipient_ids.includes(member.user_id)
                      return (
                        <button
                          key={member.user_id}
                          onClick={() => toggleRecipient(member.user_id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            isSelected ? 'bg-green-500/5' : 'hover:bg-zinc-750'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected ? 'bg-green-500 border-green-500' : 'border-zinc-600'
                          }`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate">{member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim()}</p>
                            <p className="text-xs text-zinc-500">{member.role}{member.email ? ` · ${member.email}` : ''}</p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-zinc-700">
              <button
                onClick={() => setShowCreate(false)}
                className="px-5 py-2.5 rounded-xl text-sm font-medium text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateInvoice}
                disabled={creating}
                className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 disabled:opacity-50 transition-all text-sm"
              >
                {creating ? (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {creating ? 'Sending...' : 'Send Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Connect Prompt Modal */}
      {showConnectPrompt && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowConnectPrompt(false)}>
          <div className="bg-zinc-800 rounded-2xl border border-zinc-700 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-5">
                <CreditCard className="w-8 h-8 text-amber-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Connect Stripe First</h2>
              <p className="text-zinc-400 text-sm mb-6">
                You need to set up Stripe Connect before creating invoices. This ensures payments from your team members go directly to your bank account.
              </p>
              <div className="space-y-3">
                <button
                  onClick={async () => {
                    try {
                      const res = await portalRequest(`/api/teams/${team.team_id}/connect-onboard`, { method: 'POST' })
                      if (res?.onboarding_url || res?.url) {
                        window.open(res.onboarding_url || res.url, '_blank')
                      }
                    } catch (err) {
                      alert(err.message || 'Failed to start Stripe setup')
                    }
                    setShowConnectPrompt(false)
                  }}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all text-sm"
                >
                  <CreditCard className="w-4 h-4" />
                  Set Up Stripe Connect
                </button>
                <button
                  onClick={() => setShowConnectPrompt(false)}
                  className="w-full px-5 py-3 rounded-xl text-sm font-medium text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-400 font-medium">Paid</p>
              <p className="text-lg font-bold text-white">
                {invoices.filter(i => i.status === 'paid').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-400 font-medium">Outstanding</p>
              <p className="text-lg font-bold text-white">
                {invoices.filter(i => ['pending', 'overdue', 'partial'].includes(i.status)).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-400 font-medium">Total Due</p>
              <p className="text-lg font-bold text-white">{formatCurrency(totalDue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {['all', 'pending', 'overdue', 'paid', 'partial'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              filter === f
                ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                : 'text-zinc-400 hover:text-zinc-200 border border-zinc-700 hover:border-zinc-600'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f !== 'all' && (
              <span className="ml-1.5 text-xs">
                ({invoices.filter(i => i.status === f).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Invoice List */}
      {filtered.length === 0 ? (
        <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-12 text-center">
          <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">No invoices found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(invoice => {
            const id = invoice.invoice_id || invoice._id
            const status = statusConfig[invoice.status] || statusConfig.draft
            const StatusIcon = status.icon
            const isExpanded = expandedId === id

            return (
              <div key={id} className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-zinc-750 transition-colors text-left"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${status.color}`}>
                      <StatusIcon className="w-3.5 h-3.5" />
                      {status.label}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {invoice.description || invoice.title || `Invoice #${id?.slice(-6)}`}
                      </p>
                      <p className="text-xs text-zinc-400">
                        {formatDate(invoice.created_at || invoice.date)} 
                        {invoice.due_date && ` · Due ${formatDate(invoice.due_date)}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    <span className="text-lg font-bold text-white">
                      {formatCurrency(invoice.amount || 0)}
                    </span>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-zinc-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-zinc-500" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-zinc-700 pt-4">
                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                      {invoice.description && (
                        <div className="col-span-2">
                          <p className="text-zinc-500 text-xs font-medium mb-1">Description</p>
                          <p className="text-zinc-200">{invoice.description}</p>
                        </div>
                      )}
                      {invoice.due_date && (
                        <div>
                          <p className="text-zinc-500 text-xs font-medium mb-1">Due Date</p>
                          <p className="text-zinc-200">{formatDate(invoice.due_date)}</p>
                        </div>
                      )}
                      {invoice.amount_paid > 0 && (
                        <div>
                          <p className="text-zinc-500 text-xs font-medium mb-1">Amount Paid</p>
                          <p className="text-green-400">{formatCurrency(invoice.amount_paid)}</p>
                        </div>
                      )}
                      {(invoice.amount_remaining > 0 || ['pending', 'overdue', 'partial'].includes(invoice.status)) && (
                        <div>
                          <p className="text-zinc-500 text-xs font-medium mb-1">Remaining</p>
                          <p className="text-amber-400">
                            {formatCurrency(invoice.amount_remaining || invoice.amount || 0)}
                          </p>
                        </div>
                      )}
                    </div>

                    {['pending', 'overdue', 'partial'].includes(invoice.status) && (
                      <button
                        onClick={() => handlePay(invoice)}
                        disabled={payingId === id}
                        className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 disabled:opacity-50 transition-all text-sm"
                      >
                        {payingId === id ? (
                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                        ) : (
                          <CreditCard className="w-4 h-4" />
                        )}
                        Pay Now
                      </button>
                    )}

                    {invoice.receipt_url && invoice.status === 'paid' && (
                      <a
                        href={invoice.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-green-400 hover:text-green-300 font-medium"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Receipt
                      </a>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
